import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Box, 
  CircularProgress, 
  Typography, 
  Paper, 
  Alert,
  Button,
  Container 
} from '@mui/material';
import { api } from '../services/api';

interface SessionAccessPageProps {}

const SessionAccessPage: React.FC<SessionAccessPageProps> = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sessionInfo, setSessionInfo] = useState<any>(null);

  useEffect(() => {
    if (!sessionId) {
      setError('세션 ID가 없습니다.');
      setLoading(false);
      return;
    }

    connectToSession();
  }, [sessionId]);

  const connectToSession = async () => {
    try {
      setLoading(true);
      setError(null);

      // 먼저 세션 유효성 확인
      const verifyResponse = await api.get(`/sessions/${sessionId}/verify`);
      
      if (!verifyResponse.data.can_connect) {
        setError(verifyResponse.data.message || '세션에 접속할 수 없습니다.');
        setLoading(false);
        return;
      }

      setSessionInfo(verifyResponse.data);

      // 세션에 연결
      const connectResponse = await api.post(`/sessions/${sessionId}/connect`);
      
      if (connectResponse.data.redirect_url) {
        // 개별 사용자 URL로 리다이렉트
        navigate(connectResponse.data.redirect_url, { replace: true });
      } else {
        throw new Error('리다이렉트 URL을 받지 못했습니다.');
      }

    } catch (err: any) {
      console.error('세션 연결 오류:', err);
      
      if (err.response?.status === 404) {
        setError('세션을 찾을 수 없습니다.');
      } else if (err.response?.status === 410) {
        setError('세션이 만료되었거나 비활성화되었습니다.');
      } else if (err.response?.status === 429) {
        setError('세션 접속자 수가 초과되었습니다.');
      } else {
        setError(err.response?.data?.detail || '세션 연결에 실패했습니다.');
      }
      
      setLoading(false);
    }
  };

  const handleRetry = () => {
    connectToSession();
  };

  const handleGoHome = () => {
    navigate('/', { replace: true });
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
        }}
      >
        <Container maxWidth="sm">
          <Paper
            elevation={24}
            sx={{
              padding: 4,
              borderRadius: 4,
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              textAlign: 'center',
            }}
          >
            <CircularProgress
              size={60}
              sx={{
                color: 'white',
                mb: 3,
              }}
            />
            <Typography
              variant="h5"
              sx={{
                color: 'white',
                mb: 2,
                fontWeight: 'bold',
              }}
            >
              세션에 연결 중...
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: 'rgba(255, 255, 255, 0.8)',
              }}
            >
              잠시만 기다려주세요.
            </Typography>
          </Paper>
        </Container>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
        }}
      >
        <Container maxWidth="sm">
          <Paper
            elevation={24}
            sx={{
              padding: 4,
              borderRadius: 4,
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              textAlign: 'center',
            }}
          >
            <Alert
              severity="error"
              sx={{
                mb: 3,
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                '& .MuiAlert-message': {
                  color: 'white',
                },
                '& .MuiAlert-icon': {
                  color: '#ff6b6b',
                },
              }}
            >
              {error}
            </Alert>
            
            {sessionInfo && (
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="h6"
                  sx={{
                    color: 'white',
                    mb: 1,
                    fontWeight: 'bold',
                  }}
                >
                  {sessionInfo.session_name || `세션 ${sessionId}`}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.8)',
                  }}
                >
                  현재 접속자: {sessionInfo.current_connections} / {sessionInfo.max_connections}
                </Typography>
              </Box>
            )}

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                variant="contained"
                onClick={handleRetry}
                sx={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  color: 'white',
                  '&:hover': {
                    background: 'rgba(255, 255, 255, 0.3)',
                  },
                }}
              >
                다시 시도
              </Button>
              <Button
                variant="outlined"
                onClick={handleGoHome}
                sx={{
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                  color: 'white',
                  '&:hover': {
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                    background: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                홈으로 돌아가기
              </Button>
            </Box>
          </Paper>
        </Container>
      </Box>
    );
  }

  return null;
};

export default SessionAccessPage; 